import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { GraduationCap, RefreshCw } from 'lucide-react';
import { API_BASE, getAdminToken, formatAmount } from './adminUtils';

export const CoursesTab = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCourses = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/courses`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setCourses((await res.json()).courses);
    } catch { toast.error('Failed to load courses'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCourses(); }, []);

  const toggleCourseStatus = async (courseId, currentStatus) => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/courses/toggle`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ course_id: courseId, is_enabled: !currentStatus })
      });
      if (res.ok) { toast.success((await res.json()).message); fetchCourses(); }
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2"><GraduationCap className="w-5 h-5 text-amber-400" />Course Management</CardTitle>
          <Button variant="outline" size="icon" onClick={fetchCourses} className="border-white/20 text-white"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">Course</TableHead><TableHead className="text-slate-300">Category</TableHead><TableHead className="text-slate-300">Price</TableHead><TableHead className="text-slate-300">Enrollments</TableHead><TableHead className="text-slate-300">Status</TableHead><TableHead className="text-slate-300 text-center">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {courses.map(c => (
                  <TableRow key={c.id} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{c.name}</TableCell>
                    <TableCell><Badge className="bg-slate-700 text-slate-300">{c.category}</Badge></TableCell>
                    <TableCell className="text-emerald-400">{formatAmount(c.price)}</TableCell>
                    <TableCell className="text-slate-300">{c.enrollment_count}</TableCell>
                    <TableCell><Badge className={c.is_enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}>{c.is_enabled ? 'Enabled' : 'Disabled'}</Badge></TableCell>
                    <TableCell className="text-center"><Switch checked={c.is_enabled} onCheckedChange={() => toggleCourseStatus(c.id, c.is_enabled)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
